import { Box, Grid, Typography } from "@mui/material";
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import regression from "regression";
import LineChart from "../components/chart/LineChart";
import VerticalBarChart from "../components/chart/VerticalBarChart";
import { ChartCard, M3Field, M3Surface, SegmentedButton } from "../components/m3";
import muiSytle from "../style/components-style/muiStyle";
import "../style/components-style/ScopeEmissionGraph.css";

// Register required Chart.js components
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Legend, Tooltip);

function ScopeEmissionGraph({ scopeEmissionData, columnDefs, selectedScopes = [], yearMapByColumnField = {} }) {
  const { t } = useTranslation();
  const originalGraph = { label: "Original Emissions", value: "original" };
  const regressionGraph = { label: "Regression Emissions", value: "regression" };
  const linearRegression = { label: "Linear Regression", value: "linear" };
  const polynomialRegression = { label: "Polynomial Regression", value: "polynomial" };
  const exponentialRegression = { value: "exponential", label: "Exponential" };
  const baseYear = { label: "Base Year", value: "baseYear" };
  const previousYear = { label: "Previous Year", value: "previousYear" };
  const graphTypeOptions = [originalGraph, regressionGraph];
  const regressionTypeOptions = [linearRegression, polynomialRegression, exponentialRegression];
  const differenceModeOptions = [baseYear, previousYear];
  const [predictionYear, setPredictionYear] = useState("");
  const [predictedValue, setPredictedValue] = useState(null);
  const [regressionType, setRegressionType] = useState(polynomialRegression.value);
  const [differenceMode, setDifferenceMode] = useState(baseYear.value);
  const [graphType, setGraphType] = useState(originalGraph.value);
  const [viewGrouping, setViewGrouping] = useState("standard");
  const [r2s, setR2s] = useState({});

  const processedRows = useMemo(() => {
    if (!scopeEmissionData || scopeEmissionData.length === 0) return [];

    if (viewGrouping === "standard") {
      const colFields = Object.keys(scopeEmissionData[0] || {}).filter((k) => k.startsWith("column"));

      const s1Rows = scopeEmissionData.filter((r) => {
        const s = String(r.scope || "").toLowerCase();
        return s.startsWith("scope 1") && !s.includes("+") && !s.includes("total");
      });
      const s2Rows = scopeEmissionData.filter((r) => {
        const s = String(r.scope || "").toLowerCase();
        return s.startsWith("scope 2") && !s.includes("+") && !s.includes("total");
      });
      const s3Rows = scopeEmissionData.filter((r) => {
        const s = String(r.scope || "").toLowerCase();
        return s.startsWith("scope 3") && !s.includes("+") && !s.includes("total");
      });

      const s1Row = { scope: "Scope 1" };
      const s2Row = { scope: "Scope 2" };
      const s3Row = { scope: "Scope 3" };

      colFields.forEach((field) => {
        s1Row[field] = s1Rows.reduce((sum, r) => sum + (Number(r[field]) || 0), 0);
        s2Row[field] = s2Rows.reduce((sum, r) => sum + (Number(r[field]) || 0), 0);
        s3Row[field] = s3Rows.reduce((sum, r) => sum + (Number(r[field]) || 0), 0);
      });

      return [s1Row, s2Row, s3Row];
    } else {
      // Exclude "total" rows to prevent 100x scale distortion
      return scopeEmissionData.filter((r) => {
        const s = String(r.scope || "").toLowerCase();
        if (s.includes("total")) return false;
        const colFields = Object.keys(r).filter((k) => k.startsWith("column"));
        return colFields.some((f) => Math.abs(Number(r[f]) || 0) > 0.0001);
      });
    }
  }, [scopeEmissionData, viewGrouping]);

  const activeScopes = useMemo(() => {
    if (viewGrouping === "standard") {
      return ["Scope 1", "Scope 2", "Scope 3"];
    }
    return processedRows.map((r) => r.scope).filter(Boolean);
  }, [viewGrouping, processedRows]);

  const pivotByColumns = (rows) => {
    if (!rows || rows.length === 0) return [];
    const columnFields = Object.keys(rows[0]).filter((key) => key.startsWith("column"));
    return columnFields.map((colField) => {
      let yearLabel = yearMapByColumnField?.[colField];
      if (!yearLabel && columnDefs) {
        const colDef = columnDefs.find((c) => c.field === colField);
        if (colDef?.headerName) {
          const match = colDef.headerName.match(/\b(20\d\d)\b/);
          if (match) yearLabel = match[1];
        }
      }
      const resultRow = { label: yearLabel || colField };
      rows.forEach((row) => {
        resultRow[row.scope] = Number(Number(row[colField] || 0).toFixed(2));
      });
      return resultRow;
    });
  };
  const originalData = useMemo(() => pivotByColumns(processedRows), [processedRows, columnDefs, yearMapByColumnField]);
  const regressionData = useMemo(() => {
    if (!originalData || originalData.length === 0) return [];
    const scopeKeys = Object.keys(originalData[0]).filter((key) => key !== "label");
    const years = originalData.map((d) => Number(d.label));
    const minYear = Math.min(...years);
    const maxPredictionYear = Number(predictionYear);
    if (!Number.isFinite(minYear) || !Number.isFinite(maxPredictionYear)) return [];
    if (minYear >= maxPredictionYear) return originalData;
    const modelsByScope = {};
    const r2ByScope = {};
    scopeKeys.forEach((scope) => {
      const points = originalData.map((d) => [Number(d.label) - minYear, Number(d[scope] ?? 0)]);
      if (regressionType === linearRegression.value) {
        const reg = regression.linear(points);
        modelsByScope[scope] = reg;
        r2ByScope[scope] = reg.r2;
      } else if (regressionType === polynomialRegression.value) {
        const reg = regression.polynomial(points, { order: 2 });
        modelsByScope[scope] = reg;
        r2ByScope[scope] = reg.r2;
      } else if (regressionType === exponentialRegression.value) {
        const hasPositive = points.some(([x, y]) => y > 0);

        if (!hasPositive) {
          modelsByScope[scope] = "ZERO_MODEL";
          r2ByScope[scope] = 1;
        } else {
          try {
            const reg = regression.exponential(points);
            modelsByScope[scope] = reg;
            r2ByScope[scope] = reg.r2;
          } catch (e) {
            modelsByScope[scope] = "ZERO_MODEL";
            r2ByScope[scope] = 1;
          }
        }
      } else {
        modelsByScope[scope] = null;
        r2ByScope[scope] = null;
      }
    });
    setR2s(r2ByScope);
    const result = [];
    for (let year = minYear; year <= maxPredictionYear; year++) {
      const row = { label: year };
      scopeKeys.forEach((scope) => {
        const model = modelsByScope[scope];
        if (!model) {
          row[scope] = null;
          return;
        }
        if (model === "ZERO_MODEL") {
          row[scope] = 0;
          return;
        }
        const [, y] = model.predict(year - minYear); // [x, y]
        row[scope] = y;
      });
      result.push(row);
    }
    return result;
  }, [originalData, regressionType, predictionYear]);
  const percentageDiffenrenceData = useMemo(() => {
    if (!originalData || originalData.length < 2) return [];
    const scopeKeys = Object.keys(originalData[0]).filter((k) => k !== "label");
    const result = [];
    for (let i = 1; i < originalData.length; i++) {
      const row = originalData[i];
      const referenceRow = differenceMode === baseYear.value ? originalData[0] : originalData[i - 1];
      const resultRow = { label: row.label };
      scopeKeys.forEach((scope) => {
        const current = Number(row[scope] ?? 0);
        const reference = Number(referenceRow[scope] ?? 0);
        if (!reference) {
          resultRow[scope] = 0;
        } else {
          resultRow[scope] = (100 * (current - reference)) / reference;
        }
      });
      result.push(resultRow);
    }
    return result;
  }, [originalData, differenceMode]);
  const percentageDifferenceDomain = useMemo(() => {
    if (!percentageDiffenrenceData || percentageDiffenrenceData.length < 1) return { min: -100, max: 0 };
    const scopeKeys = Object.keys(percentageDiffenrenceData[0]).filter((k) => k !== "label");
    const allValues = percentageDiffenrenceData.flatMap((row) => scopeKeys.map((scope) => row[scope]));
    let min = Math.min(...allValues);
    let max = Math.max(...allValues);
    min = Math.floor(min / 100) * 100;
    max = Math.ceil(max / 100) * 100;
    if (min === max) {
      max += 100;
    }
    return { min, max };
  }, [percentageDiffenrenceData]);

  // Suppression flags (suppress display without deleting underlying logic)
  const suppressPercentageDifference = true;
  const suppressRegression = true;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("Emission Graphs")}
        </Typography>

        <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
              {t("Grouping")}:
            </Typography>
            <SegmentedButton
              options={[
                { value: "standard", label: t("Standard Scopes (1, 2, 3)") },
                { value: "categories", label: t("All Categories") },
              ]}
              value={viewGrouping}
              onChange={setViewGrouping}
            />
          </Box>

          {!suppressRegression && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {t("Type")}:
              </Typography>
              <SegmentedButton
                options={[
                  { value: "original", label: t("Original Graph") },
                  { value: "regression", label: t("Regression Graph") },
                ]}
                value={graphType}
                onChange={setGraphType}
              />
            </Box>
          )}

          {!suppressRegression && graphType === regressionGraph.value && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {t("Model")}:
              </Typography>
              <SegmentedButton
                options={[
                  { value: "polynomial", label: t("Polynomial") },
                  { value: "linear", label: t("Linear") },
                  { value: "exponential", label: t("Exponential") },
                ]}
                value={regressionType}
                onChange={setRegressionType}
              />
            </Box>
          )}

          {!suppressRegression && graphType === regressionGraph.value && (
            <Box sx={{ minWidth: "140px", maxWidth: "160px" }}>
              <M3Field
                placeholder={t("Target Year")}
                type="number"
                value={predictionYear}
                onChange={(val) => {
                  setPredictionYear(parseInt(val, 10) || "");
                }}
              />
            </Box>
          )}

          {!suppressPercentageDifference && percentageDiffenrenceData.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {t("Baseline")}:
              </Typography>
              <SegmentedButton
                options={[
                  { value: "baseYear", label: t("Base Year") },
                  { value: "previousYear", label: t("Previous Year") },
                ]}
                value={differenceMode}
                onChange={setDifferenceMode}
              />
            </Box>
          )}
        </Box>
      </Box>

      {!suppressRegression && predictedValue !== null && graphType === regressionGraph.value && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Predicted Emissions in {predictionYear} years:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {predictedValue.value}
            </Box>
          </Typography>
          {predictedValue.confidenceInterval && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Confidence Interval (95%):{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                [{predictedValue.confidenceInterval.lower}, {predictedValue.confidenceInterval.upper}]
              </Box>
            </Typography>
          )}
        </Box>
      )}

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        <Grid size={{ xs: 12, md: (!suppressPercentageDifference && percentageDiffenrenceData.length > 0) ? 6 : 12 }} sx={{ display: "flex" }}>
          <ChartCard
            title={graphType === originalGraph.value ? t("Original Emissions Graph") : t("Regression Emissions Graph")}
            style={{ flex: 1 }}
          >
            <LineChart
              dataLabel="label"
              data={graphType === originalGraph.value ? originalData : regressionData}
              datakeys={activeScopes}
              YAxisLabel={t("Scope Emissions (tCO₂e)")}
              XAxisLabel={t("Years")}
              type={graphType === originalGraph.value ? "linear" : "monotone"}
            />
          </ChartCard>
        </Grid>
        {!suppressPercentageDifference && percentageDiffenrenceData.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <ChartCard
              title={differenceMode === baseYear.value ? t("Percentage Difference from Base Year") : t("Percentage Difference from Previous Year")}
              style={{ flex: 1, height: "100%" }}
            >
              <VerticalBarChart
                data={percentageDiffenrenceData}
                dataLabel={{ key: "label", label: "label" }}
                dataKeys={activeScopes.map((s) => ({ key: s, label: s }))}
                XAxisLabel="Year"
                YAxisLabel="Percentage Change (%)"
                domainMin={percentageDifferenceDomain.min}
                domainMax={percentageDifferenceDomain.max}
                unit="%"
              />
            </ChartCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default ScopeEmissionGraph;
